import {
  Align,
  Button,
  Container,
  List,
  Loader,
  Spacer,
  Split,
  SplitSection,
  SvgAdd,
  SvgArrowRight,
  TextButton,
} from '@loomhq/lens';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';

import { LARGE_TABLET_MAX_WIDTH } from 'constants/breakpoints';
import {
  ONBOARDING_STEP_COMPLETE,
  ONBOARDING_STEP_NO_SUGGESTION,
  ONBOARDING_STEP_SKIPPED,
} from 'constants/metrics';
import * as analytics from 'utilities/analytics';
import { incrementMetric } from 'utilities/metrics';
import {
  getSuggestedWorkspacesQuery,
  requestToJoinWorkspace,
} from 'utilities/welcome';

import WorkspaceListItem, { SUGGESTED_LABEL } from './WorkspaceListItem';
import {
  JOIN_REQUEST_STATES,
  SUGGESTED_WORKSPACES_STEP,
} from '../../constants';
import { FixedMobileFooter } from '../../cta';
import { PageHeader, PageSubHeader } from '../../headers';
import { COMPLETED_ONBOARDING_STEP } from '../../../../constants/events';
import { PRIMARY_WEB_APP } from '../../../../constants/onboarding';
import {
  REQUEST_TO_JOIN_WORKSPACE_AUTOJOINED,
  REQUEST_TO_JOIN_WORKSPACE_REQUESTED,
} from '../../../../../../shared/constants/organizationInvitations';

import MediaQuery from 'components/media-query';

import styles from './styles.module.less';

function SuggestedWorkspaces({ nextClickHandler, isMobile }) {
  const [workspaces, setWorkspaces] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function getSuggestedWorkspaces() {
      const { data } = await getSuggestedWorkspacesQuery();

      if (!data?.result?.workspaces?.length) {
        // No suggested workspaces, move on to the next step.
        analytics.track(COMPLETED_ONBOARDING_STEP, {
          step: SUGGESTED_WORKSPACES_STEP,
          action: 'no suggestions',
          is_simiplified_onboarding: true,
          source: PRIMARY_WEB_APP,
        });

        incrementMetric(ONBOARDING_STEP_NO_SUGGESTION, {
          step: SUGGESTED_WORKSPACES_STEP,
          source: PRIMARY_WEB_APP,
        });

        nextClickHandler();
      }

      setWorkspaces(data);
      setLoading(false);
    }

    getSuggestedWorkspaces();
  }, [nextClickHandler]);

  function skip() {
    nextClickHandler({ joinState: JOIN_REQUEST_STATES.SKIPPED });
    analytics.track(COMPLETED_ONBOARDING_STEP, {
      step: SUGGESTED_WORKSPACES_STEP,
      action: 'skipped',
      is_simiplified_onboarding: true,
      source: PRIMARY_WEB_APP,
    });

    incrementMetric(ONBOARDING_STEP_SKIPPED, {
      step: SUGGESTED_WORKSPACES_STEP,
      source: PRIMARY_WEB_APP,
    });
  }

  async function onRequestClick(workspaceId) {
    const {
      data: { result },
    } = await requestToJoinWorkspace(workspaceId);

    const eventProperties = {
      step: SUGGESTED_WORKSPACES_STEP,
      organization_id: workspaceId,
      is_simiplified_onboarding: true,
      source: PRIMARY_WEB_APP,
    };

    if (
      [
        REQUEST_TO_JOIN_WORKSPACE_REQUESTED,
        REQUEST_TO_JOIN_WORKSPACE_AUTOJOINED,
      ].includes(result.status)
    ) {
      if (result.status === REQUEST_TO_JOIN_WORKSPACE_REQUESTED) {
        nextClickHandler({ joinState: JOIN_REQUEST_STATES.REQUESTED });
        eventProperties.action = 'requested';
      } else {
        nextClickHandler({ joinState: JOIN_REQUEST_STATES.AUTOJOINED });
        eventProperties.action = 'autojoined';
      }

      analytics.track(COMPLETED_ONBOARDING_STEP, eventProperties);

      incrementMetric(ONBOARDING_STEP_COMPLETE, {
        step: SUGGESTED_WORKSPACES_STEP,
        source: PRIMARY_WEB_APP,
      });
    }
  }

  if (!workspaces || loading) {
    return (
      <Align alignment="center">
        <Loader size="large" />
      </Align>
    );
  }

  return (
    <Spacer left={1} right={1}>
      <Split direction="column" alignItems="flex-start">
        <SplitSection>
          <PageHeader text="We found your team on Loom!" />
          <Spacer top="large" bottom={{ default: 'large', medium: 'xlarge' }}>
            <PageSubHeader
              text="Join your team's Workspace and gain instant access to team
              videos and see deeper video insights."
            />
          </Spacer>
        </SplitSection>

        {workspaces?.result && (
          <SplitSection grow={1} width="100%" minHeight="0">
            <WorkspaceList
              workspaces={workspaces.result.workspaces}
              onRequestClick={onRequestClick}
              onSkip={skip}
              isMobile={isMobile}
              isSimplifiedOnboarding={true}
            />
            <Spacer bottom={{ default: 16.5, medium: 0 }} />
          </SplitSection>
        )}
      </Split>
    </Spacer>
  );
}

export function WorkspaceList({
  workspaces,
  onRequestClick,
  onSkip,
  isMobile,
  isSimplifiedOnboarding,
}) {
  const [showAll, setShowAll] = useState(false);

  // The selection state is only used on mobile, where we require the user to tap a workspace and then
  // confirm their selection at the bottom of the screen.
  const [selection, setSelection] = useState();

  let content;

  if (showAll || workspaces.length < 2) {
    content = (
      <>
        {workspaces.map((workspace, i) => (
          <WorkspaceListItem
            key={workspace.workspace.id}
            {...workspace}
            isSuggested={i === 0}
            onRequestClick={onRequestClick}
            isMobile={isMobile}
            isSelected={workspace.workspace.id === selection?.id}
            onMobileSelect={setSelection}
          />
        ))}
      </>
    );
  } else {
    content = (
      <>
        <WorkspaceListItem
          {...workspaces[0]}
          isSuggested
          onRequestClick={onRequestClick}
          isMobile={isMobile}
          isSelected={workspaces[0].workspace.id === selection?.id}
          onMobileSelect={setSelection}
          additionalClass={styles.firstListRow}
        />
        <div className={styles.seeMoreWorkspaceBtn}>
          <Align alignment="center">
            <TextButton onClick={() => setShowAll(true)}>
              See more Workspaces
            </TextButton>
          </Align>
        </div>
      </>
    );
  }

  return (
    <>
      {isMobile && (
        <Spacer>
          <span>{SUGGESTED_LABEL}</span>
        </Spacer>
      )}
      {isMobile && (
        <>
          <List variant="border">{content}</List>
        </>
      )}
      {!isMobile && (
        <Container
          className={classnames(
            'sm-border sm-radius:medium mt:medium',
            styles.listContainer
          )}
        >
          <List variant="border">{content}</List>
        </Container>
      )}

      {isSimplifiedOnboarding && (
        <>
          <MediaQuery query={`(min-width: ${LARGE_TABLET_MAX_WIDTH}px)`}>
            <Spacer top={2} bottom={{ default: 0, medium: 2 }}>
              <Button
                hasFullWidth
                onClick={onSkip}
                id="skipJoinWorkspaceButton"
                icon={<SvgAdd />}
              >
                Create a new Workspace instead
              </Button>
            </Spacer>
          </MediaQuery>

          <FixedMobileFooter>
            <Split direction="column" width="100%" gap={2}>
              <SplitSection width="100%">
                <Button
                  hasFullWidth
                  variant="primary"
                  disabled={!selection}
                  onClick={() => onRequestClick(selection.id)}
                  size="large"
                  icon={<SvgArrowRight />}
                  iconPosition="right"
                >
                  {!selection || selection.autoJoin
                    ? 'Join workspace and continue'
                    : 'Request access'}
                </Button>
              </SplitSection>

              <SplitSection width="100%">
                <TextButton
                  hasFullWidth
                  onClick={onSkip}
                  id="skipJoinWorkspaceButton"
                >
                  Create a new Workspace instead
                </TextButton>
              </SplitSection>
            </Split>
          </FixedMobileFooter>
        </>
      )}
    </>
  );
}

export default SuggestedWorkspaces;
